<script>
import LucideIcon from '../../../../../components/LucideIcon.vue';

export default {
  name: 'UpgradePlanCard',
  components: { LucideIcon },
  props: {
    plan: { type: Object, required: true },
  },
  emits: ['action'],
  computed: {
    outerClasses() {
      const base =
        'group relative flex flex-col h-full rounded-3xl shadow-sm pricing-card overflow-visible transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md dark:hover:shadow-slate-900/30';
      return [base, 'border border-slate-100 dark:border-n-vars-black-bd'].join(' ');
    },
    cardInnerClasses() {
      return 'relative z-10 flex flex-col h-full rounded-3xl bg-white/95 dark:bg-slate-900/95 shadow-sm backdrop-blur-sm overflow-hidden pt-10 px-8 pb-8';
    },
  },
  methods: {
    onClick() {
      if (!this.plan?.action || this.plan.action.disabled) return;
      this.$emit('action', this.plan);
    },
  },
};
</script>

<template>
  <article :class="outerClasses">
    <!-- Badge moved outside inner container to avoid clipping -->
    <div v-if="plan.badge" class="absolute z-20 -top-4 left-1/2 -translate-x-1/2">
      <span class="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-hilab to-hilab/80 shadow-lg border border-hilab/20">
        {{ plan.badge.label || plan.badge }}
      </span>
    </div>
    <div :class="cardInnerClasses">
      <div
        v-if="plan.badge"
        aria-hidden
        class="pointer-events-none absolute inset-0"
      >
        <div class="absolute inset-0 bg-gradient-to-br from-n-main-1/20 via-transparent to-transparent opacity-90"></div>
        <div class="absolute -top-16 right-1 h-48 w-48 rounded-full bg-gradient-to-br from-n-main-1/25 via-transparent to-transparent blur-3xl"></div>
        <div class="absolute top-16 left-4 h-36 w-36 rounded-full bg-gradient-to-tr from-n-main-1/15 via-transparent to-transparent blur-[70px]"></div>
      </div>

    <!-- Header -->
    <div class="pb-6 text-center">
      <h3 class="text-lg font-semibold text-slate-900 md:text-xl">{{ plan.name }}</h3>
      <p v-if="plan.description" class="mt-1 text-sm text-slate-500">{{ plan.description }}</p>
    </div>

    <!-- Pricing -->
    <div class="pb-6">
      <div class="text-center">
        <!-- Yearly -->
        <div v-if="plan.pricing.yearly" class="mb-4">
          <div class="flex items-baseline justify-center gap-1">
            <span v-if="plan.pricing.yearly.symbol" class="text-base font-medium text-slate-500">{{ plan.pricing.yearly.symbol }}</span>
            <span class="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">{{ plan.pricing.yearly.amount }}</span>
            <span v-if="plan.pricing.yearly.suffix" class="text-sm font-medium text-slate-500">{{ plan.pricing.yearly.suffix }}</span>
          </div>
          <p class="mt-1 text-[11px] uppercase tracking-wider text-slate-400">{{ plan.pricing.yearly.label }}</p>
          <p v-if="plan.pricing.note" class="mt-2 inline-block rounded-full bg-gradient-to-r from-hilab/10 to-hilab/5 px-4 py-1.5 text-[11px] font-semibold text-hilab border border-hilab/20">{{ plan.pricing.note }}</p>
        </div>

        <!-- Monthly -->
        <div v-if="plan.pricing.monthly" :class="['pt-4', plan.pricing.yearly ? 'border-t border-slate-100' : '']">
          <div class="flex items-baseline justify-center gap-1">
            <span v-if="plan.pricing.monthly.symbol" class="text-sm font-medium text-slate-500">{{ plan.pricing.monthly.symbol }}</span>
            <span class="text-lg font-semibold text-slate-700">{{ plan.pricing.monthly.amount }}</span>
            <span v-if="plan.pricing.monthly.suffix" class="text-xs font-medium text-slate-500">{{ plan.pricing.monthly.suffix }}</span>
          </div>
          <p class="mt-1 text-[11px] uppercase tracking-wider text-slate-400">{{ plan.pricing.monthly.label }}</p>
        </div>

        <!-- Fallbacks -->
        <div v-if="!plan.pricing.yearly && !plan.pricing.monthly">
          <div v-if="plan.pricing.free" class="inline-flex items-center justify-center rounded-full border border-green-300/70 bg-green-50/70 px-3 py-1 text-[13px] font-medium text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-200">{{ $t('UPGRADE_SETTINGS.PRICING.FREE') }}</div>
          <div v-else class="inline-flex items-center justify-center rounded-full border border-slate-300/80 bg-white/80 px-3 py-1 text-[13px] font-medium text-slate-600 dark:border-slate-600 dark:bg-slate-800/60">{{ $t('UPGRADE_SETTINGS.PRICING.CONTACT') }}</div>
        </div>
      </div>
    </div>

    <!-- Features/Limits -->
    <div class="flex-1 pb-6">
      <ul v-if="plan.limits.length" class="space-y-3">
        <li v-for="limit in plan.limits" :key="limit.key" class="flex items-start gap-3">
          <div class="flex-shrink-0 mt-0.5"><LucideIcon name="check-check" :size="16" class="text-hilab" /></div>
          <span class="text-sm leading-relaxed">
            <span class="font-normal text-slate-600 dark:text-slate-300">{{ limit.label }}</span>
            <span class="mx-1 text-slate-400 dark:text-slate-500">:</span>
            <span class="font-semibold text-slate-900 dark:text-slate-100">{{ limit.value }}</span>
          </span>
        </li>
      </ul>
      <p v-else class="text-sm text-slate-500">{{ $t('UPGRADE_SETTINGS.NO_LIMITS') }}</p>
    </div>

    <!-- CTA Button -->
    <div class="pt-0">
      <button
        type="button"
        :disabled="plan.action.disabled"
        @click="onClick"
        :class="[
          'group relative block w-full rounded-xl py-3 px-4 text-center text-sm font-semibold transition-all duration-300 overflow-hidden',
          plan.badge ? 'bg-gradient-to-r from-hilab to-hilab/90 text-white hover:from-hilab-dark hover:to-hilab shadow-lg hover:shadow-xl hover:scale-105' : 'bg-gradient-to-r from-slate-900 to-slate-800 text-white hover:from-slate-800 hover:to-slate-700 shadow-md hover:shadow-lg hover:scale-105',
          plan.action.disabled ? 'opacity-60 cursor-not-allowed' : ''
        ]"
      >
        <span class="relative z-10">{{ plan.action.label }}</span>
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
      </button>
    </div>
    </div>
  </article>
</template>




